<template>
  <div v-rect-loading="loading" class="notification-editor">
    <div class="header">
      <div class="title">{{ $t(`${titleI18nKey}`) }}</div>
    </div>
    <div class="content">
      <WrappedFrom
        ref="formEditor"
        :model="form"
        :formLines="formLines"
      >
        <template #tinyMceEditor="{ lineName, line }">
          <div class="rich-text">
            <TinyMceEditor
              v-if="showTinyMceEditor"
              v-model="form[lineName]"
              isLayout
              showTemplateField
              :isCnContent="line.isCnContent"
              class="edit-cn"
              :templateFields="templateFields"
              :disabled="isViewMode"
              :companyId="companyId"
              :rootCompanyId="rootCompanyId"
            >
            </TinyMceEditor>
          </div>
          <div class="send-email-test-button">
            <el-button type="primary" :disabled="isViewMode" @click="sendEmailTest(!line.isCnContent)">{{ $t(`${i18nPrefix}.btn.sendEmailTestButton`) }}</el-button>
          </div>
        </template>
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="toTemplateList">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="save-btn" :disabled="isViewMode" @click="saveOrUpdateTemplate">{{ $t('common.btn.save') }}</el-button>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import _ from 'lodash'
import { addI18nProperty } from '@/i18n'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import TinyMceEditor from '../tinyMceEditor.vue'
import { ensureEditableEmailBody } from './editableContent'

export default {
  components: {
    TinyMceEditor,
    WrappedFrom
  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.notification',
      showTinyMceEditor: false,
      layoutTemplateOptions: [],
      form: {},
      templateFields: [],
      rootCompanyId: null,
      companyId: null
    }
  },
  computed: {
    titleI18nKey () {
      return this.$route.meta.titleI18nKey
    },
    templateId () {
      return this.$route.params.templateId
    },
    systemTemplateId () {
      return this.$route.params.systemTemplateId
    },
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    isViewMode () {
      return this.$route.meta.isViewMode
    },
    refForm () {
      return this.$refs.formEditor
    },
    formLines () {
      const hasI18n = this.form.has_i18n
      const requiredMessage = this.$t('common.message.requiredFormField')
      return {
        layout_id: {
          elType: 'el-select',
          option: {
            data: this.layoutTemplateOptions
          },
          events: {
            change: this.loadLayoutTemplate
          },
          disabled: !hasI18n || this.isViewMode,
          label: this.$t(`${this.i18nPrefix}.label.layoutTemplateLoad`)
        },
        templateTypeName: {
          label: this.$t(`${this.i18nPrefix}.label.templateType`),
          disabled: !!this.templateId || !!this.systemTemplateId || this.isViewMode,
          rules: { required: true, message: requiredMessage, trigger: 'blur' }
        },
        name: {
          label: this.$t(`${this.i18nPrefix}.label.templateName`),
          disabled: this.isViewMode,
          rules: { required: true, message: requiredMessage, trigger: 'blur' }
        },
        subject_cn: {
          label: hasI18n ? this.$t(`${this.i18nPrefix}.label.subjectCn`) : this.$t(`${this.i18nPrefix}.label.subject`),
          disabled: this.isViewMode,
          rules: { required: true, message: requiredMessage, trigger: 'blur' }
        },
        subject_en: {
          label: this.$t(`${this.i18nPrefix}.label.subjectEn`),
          disabled: this.isViewMode,
          hidden: !hasI18n
        },
        content_cn: {
          contentSlot: 'tinyMceEditor',
          isCnContent: true,
          label: this.$t(`${this.i18nPrefix}.label.${hasI18n ? 'emailEditCn' : 'emailEdit'}`)
        },
        content_en: {
          hidden: !hasI18n,
          contentSlot: 'tinyMceEditor',
          label: this.$t(`${this.i18nPrefix}.label.emailEditEn`)
        }
      }
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions('notificationTemplate', ['updateTemplate', 'getTemplateById', 'createTemplate', 'searchLayoutTemplate', 'getLayoutTemplateById', 'searchTemplateField', 'sendEmail']),
    async initData () {
      this.loading = true
      try {
        await Promise.all([
          this.loadTemplate(),
          this.getLayoutTemplateList()
        ])
      } finally {
        this.loading = false
      }
    },
    async saveOrUpdateTemplate () {
      let result = false
      result = await this.refForm.submit()
      if (!result) {
        return
      }
      if (this.form.layout_id === -1) {
        this.form.layout_id = null
      }
      const params = {
        ...this.form,
        id: this.templateId,
        system_template: false
      }
      let resp = null
      this.$eventBus.$emit('showLoading', true)
      try {
        if (this.isEditMode) {
          resp = await this.updateTemplate(params)
        } else {
          resp = await this.createTemplate(params)
        }
        if (resp) {
          this.toTemplateList()
        }
      } catch (error) {
        if (!this.form.layout_id) {
          this.form.layout_id = -1
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async loadTemplate () {
      if (this.templateId || this.systemTemplateId) {
        const templateId = this.templateId != null ? this.templateId : this.systemTemplateId
        const resp = await this.getTemplateById(templateId)
        if (resp) {
          this.rootCompanyId = resp.root_company_id
          this.companyId = resp.company_id
          addI18nProperty(resp, 'templateTypeName', 'template_type_name_cn', 'template_type_name_en')
          if (!resp.layout_id) {
            resp.layout_id = -1
          }
          resp.content_cn = ensureEditableEmailBody(resp.content_cn)
          resp.content_en = ensureEditableEmailBody(resp.content_en)
          this.form = resp
        }
      }
      await this.loadTemplateField()
    },
    async sendEmailTest (isEn) {
      if (this.form.layout_id === -1) {
        this.form.layout_id = null
      }
      const params = {
        ...this.form,
        is_en: isEn
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.sendEmail(params)
        if (resp) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.sendSuccess`))
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async getLayoutTemplateList () {
      const resp = await this.searchLayoutTemplate()
      if (resp) {
        const layoutTemplateOptions = _.map(resp, (layoutTemplate) => {
          return {
            value: layoutTemplate.id,
            label: layoutTemplate.name
          }
        })
        this.layoutTemplateOptions = [
          {
            value: -1,
            label: this.$t(`${this.i18nPrefix}.label.searchUpLayout`)
          },
          ...layoutTemplateOptions]
      }
    },
    toTemplateList () {
      this.$router.push({
        path: '/v-admin/notification-management',
        query: this.$route.query
      })
    },
    async loadTemplateField () {
      const resp = await this.searchTemplateField(this.form.template_type_id)
      if (resp) {
        this.templateFields = _.map(resp, (templateField) => {
          addI18nProperty(templateField, 'i18nTitle', 'title_cn', 'title_en')
          templateField.description = templateField.description || ''
          return templateField
        })
        this.showTinyMceEditor = true
      }
    },
    async loadLayoutTemplate () {
      if (!this.form.layout_id) {
        return
      }
      const layoutTemplateId = this.form.layout_id
      const resp = await this.getLayoutTemplateById(layoutTemplateId)
      this.form.sender_name_en = resp.sender_name_en
      this.form.sender_name_cn = resp.sender_name_cn
      this.form.email_sender_address = resp.email_sender_address
      const emailHeader = '</email-header>'
      const emailFooter = '<email-footer>'
      const emailHeaderLength = emailHeader.length
      if (resp && this.form.has_i18n) {
        let contentCn = this.form.content_cn
        if (contentCn.lastIndexOf(emailHeader) !== -1) {
          contentCn = contentCn.slice(contentCn.lastIndexOf(emailHeader) + emailHeaderLength, contentCn.length)
        }
        if (contentCn.lastIndexOf(emailFooter) !== -1) {
          contentCn = contentCn.slice(0, contentCn.lastIndexOf(emailFooter))
        }
        this.form.content_cn = ensureEditableEmailBody([resp.content_header_cn, contentCn, resp.content_footer_cn].join(''))

        let contentEn = this.form.content_en
        if (contentEn.lastIndexOf(emailHeader) !== -1) {
          contentEn = contentEn.slice(contentEn.lastIndexOf(emailHeader) + emailHeaderLength, contentEn.length)
        }
        if (contentEn.lastIndexOf(emailFooter) !== -1) {
          contentEn = contentEn.slice(0, contentEn.lastIndexOf(emailFooter))
        }
        this.form.content_en = ensureEditableEmailBody([resp.content_header_en, contentEn, resp.content_footer_en].join(''))
      }
    }
  },
  watch: {
    '$i18n.locale': {
      handler () {
        this.showTinyMceEditor = false
        this.$nextTick(() => {
          this.showTinyMceEditor = true
        })
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.notification-editor {
  .header {
    border-bottom: 1px solid #e4e7ed;

    .title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .content {
    width: 980px;
    margin: 30px auto 0;

    .send-email-test-button {
      margin-top: 20px;
      text-align: right;
    }

    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;
      }

      .save-btn {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
</style>
